import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Settings, Save, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';

const SCOPE3_CATEGORIES = [
  { num: 1, name: 'Purchased Goods & Services', desc: 'Emissions from production of purchased goods and services' },
  { num: 2, name: 'Capital Goods', desc: 'Emissions from production of capital equipment' },
  { num: 3, name: 'Fuel & Energy Related', desc: 'Upstream emissions from fuel and energy production' },
  { num: 4, name: 'Upstream Transportation', desc: 'Emissions from transportation of purchased goods' },
  { num: 5, name: 'Waste Generated in Operations', desc: 'Emissions from waste disposal and treatment' },
  { num: 6, name: 'Business Travel', desc: 'Emissions from employee business travel' },
  { num: 7, name: 'Employee Commuting', desc: 'Emissions from employee commuting' },
  { num: 8, name: 'Upstream Leased Assets', desc: 'Emissions from operation of leased assets' },
  { num: 9, name: 'Downstream Transportation', desc: 'Emissions from transportation of sold products' },
  { num: 10, name: 'Processing of Sold Products', desc: 'Emissions from processing by downstream companies' },
  { num: 11, name: 'Use of Sold Products', desc: 'Emissions from end use of sold products' },
  { num: 12, name: 'End-of-Life Treatment', desc: 'Emissions from disposal of sold products' },
  { num: 13, name: 'Downstream Leased Assets', desc: 'Emissions from operation of leased assets' },
  { num: 14, name: 'Franchises', desc: 'Emissions from franchise operations' },
  { num: 15, name: 'Investments', desc: 'Emissions from investments' },
];

function GHGSettings() {
  const [settings, setSettings] = useState({
    control_approach: 'operational',
    base_year: 2024,
    gwp_standard: 'AR5',
    scope2_method: 'location',
    scope3_categories: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/ghg/settings');
      const s = res.data.settings;
      setSettings({
        ...s,
        scope3_categories: Array.isArray(s.scope3_categories) ? s.scope3_categories : JSON.parse(s.scope3_categories || '[]'),
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (num) => {
    const current = settings.scope3_categories || [];
    const updated = current.includes(num) ? current.filter(n => n !== num) : [...current, num];
    setSettings({ ...settings, scope3_categories: updated });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/ghg/settings', settings);
      toast.success('Settings saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: '#0A1628', margin: '0 0 4px' }}>GHG Settings</h1>
          <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Configure your organizational boundary and reporting methodology</div>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {saving ? <RefreshCw size={14} /> : <Save size={14} />} Save Settings
        </button>
      </div>

      {/* Org boundary */}
      <div className="card" style={{ padding: '20px', marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 4 }}>Organizational Boundary</div>
        <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)', marginBottom: 16 }}>Define which entities and operations are included in your GHG inventory per GHG Protocol</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { value: 'operational', label: 'Operational Control', desc: 'Include operations where you have full authority to implement operating policies. Most common for industrial facilities.' },
            { value: 'financial', label: 'Financial Control', desc: 'Include operations where you have the ability to direct financial and operating policies to gain economic benefits.' },
            { value: 'equity_share', label: 'Equity Share', desc: 'Include operations proportional to your equity share in the operation.' },
          ].map(opt => (
            <div key={opt.value} onClick={() => setSettings({...settings, control_approach: opt.value})}
              style={{ padding: '14px', borderRadius: 10, border: `2px solid ${settings.control_approach === opt.value ? '#0066FF' : '#E2E8F0'}`, background: settings.control_approach === opt.value ? '#EFF6FF' : 'white', cursor: 'pointer' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: settings.control_approach === opt.value ? '#0066FF' : '#0A1628', fontFamily: 'var(--font-body)', marginBottom: 6 }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)', lineHeight: 1.5 }}>{opt.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reporting settings */}
      <div className="card" style={{ padding: '20px', marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 16 }}>Reporting Configuration</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Base Year</label>
            <select value={settings.base_year} onChange={e => setSettings({...settings, base_year: parseInt(e.target.value)})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
              {[2019,2020,2021,2022,2023,2024].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-body)', marginTop: 4 }}>Year against which reductions are measured</div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>GWP Standard</label>
            <select value={settings.gwp_standard} onChange={e => setSettings({...settings, gwp_standard: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
              <option value="AR5">IPCC AR5 (EPA default)</option>
              <option value="AR6">IPCC AR6 (latest)</option>
            </select>
            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-body)', marginTop: 4 }}>Global Warming Potential values used for CO2e calculation</div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', fontFamily: 'var(--font-body)', display: 'block', marginBottom: 4 }}>Scope 2 Method</label>
            <select value={settings.scope2_method} onChange={e => setSettings({...settings, scope2_method: e.target.value})}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'var(--font-body)', fontSize: 14 }}>
              <option value="location">Location-based (eGRID grid average)</option>
              <option value="market">Market-based (RECs, green tariffs)</option>
              <option value="both">Both (dual reporting)</option>
            </select>
            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-body)', marginTop: 4 }}>How to calculate Scope 2 electricity emissions</div>
          </div>
        </div>
      </div>

      {/* Scope 3 categories */}
      <div className="card" style={{ padding: '20px' }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 4 }}>Scope 3 Categories</div>
        <div style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)', marginBottom: 16 }}>Select which Scope 3 categories are relevant to your organization. Only enabled categories will appear in the inventory.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SCOPE3_CATEGORIES.map(cat => {
            const enabled = (settings.scope3_categories || []).includes(cat.num);
            return (
              <div key={cat.num} onClick={() => toggleCategory(cat.num)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1px solid ${enabled ? '#0066FF' : '#E2E8F0'}`, background: enabled ? '#EFF6FF' : 'white', cursor: 'pointer' }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${enabled ? '#0066FF' : '#CBD5E1'}`, background: enabled ? '#0066FF' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {enabled && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: enabled ? '#0066FF' : '#0A1628', fontFamily: 'var(--font-body)' }}>
                    Category {cat.num} — {cat.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--font-body)' }}>{cat.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GHGSettings;
